import { AnyObject, Maybe } from '@vimeo/core/types/generic';

import { environmentConfig } from '../../configs/EnvironmentConfig';
import { liveApplicationConfig } from '../../configs/LiveApplicationConfig';
import { liveTrackingConfig } from '../../configs/LiveTrackingConfig';

/**
 * Error categories for easier Datadog filtering and checking error source.
 */
export enum ELiveErrorCategory {
  LIVE = 'LIVE',
  GENERIC = 'GENERIC',
  AGORA = 'AGORA',
  MEDIA = 'MEDIA',
  GUEST = 'GUEST',
  INTERACTION = 'INTERACTION',
  GRAPHICS = 'GRAPHICS',
  FIREBASE = 'FIREBASE',
  RTMP = 'RTMP',
}

/**
 * Some generic information in a same format for whole page.
 */
export interface ILiveErrorAdditionalInfo {
  category: ELiveErrorCategory;
  method?: string;
  component?: string;
  data?: AnyObject;
}

/**
 * Used to measure page performance/state/context stats.
 *
 * @returns shared contextual info to be sent with every tracked event
 */
export function getLiveApplicationInfo(): AnyObject {
  const memory: Maybe<AnyObject> = (window.performance as any)['memory'];

  return {
    // Inject live application globals to make sure it works with all subdomains and embeds where Datadog does not expose globals.
    ...getLiveApplicationGlobals(),
    /**
     * Page state fields allowing to estimate in which state error happens.
     * It can happen when tab is focused/focused out, visible or not visible to user etc.
     * Idle timing can help us to estimate whether user is interacting with page or not.
     *
     * Providing custom fields is better than bundled JSON because you can do queries on attributes.
     */
    pageLifeDuration: Date.now() - liveApplicationConfig.TIMING.STARTED_AT,
    pageIdleTime: liveApplicationConfig.TIMING.IDLE_TIME,
    pageIsActive: liveApplicationConfig.IS_ACTIVE,
    pageIsVisible: !window.document.hidden,
    pageIsFocused: window.document.hasFocus(),
    pageErrorsTracked: liveApplicationConfig.TRACKING.NR_LOG_ERRORS_TRACKED,
    /** General info. */
    pageSessionCloudId: liveTrackingConfig.DETAILS.SESSION_CLOUD_ID,
    pageSessionUserRole: liveTrackingConfig.DETAILS.SESSION_USER_ROLE,
    /** State fields. */
    pageEventState: liveTrackingConfig.BIG_PICTURE_LIVE_SCHEMA_BASE.liveStatus,
    /** Page memory state. */
    usedJSHeapSize: Math.floor((memory?.usedJSHeapSize || 0) / 1024 / 1024),
    totalJSHeapSize: Math.floor((memory?.totalJSHeapSize || 0) / 1024 / 1024),
    jsHeapSizeLimit: Math.floor((memory?.jsHeapSizeLimit || 0) / 1024 / 1024),
  };
}

/**
 * @returns shared globals information to share for all events
 */
export function getLiveApplicationGlobals(): AnyObject {
  const globals: AnyObject = {
    pageCombinedProviders: liveApplicationConfig.USE_COMBINED_PROVIDERS,
    pageSessionApplication: liveTrackingConfig.DETAILS.SESSION_APPLICATION,
    pageSessionType: liveTrackingConfig.DETAILS.SESSION_TYPE,
    pageSessionId: liveTrackingConfig.DETAILS.SESSION_ID,
    pageSessionLocale: liveTrackingConfig.DETAILS.USER_LOCALE,
    pageSessionUuid: liveTrackingConfig.DETAILS.SESSION_PAGE_UUID,
    buildId: environmentConfig.BUILD_ID,
  };

  /** IFraming related globals. */
  if (environmentConfig.IS_IFRAME) {
    globals.pageIsIframe = true;
    globals.pageIframeReferer = String(window?.document.referrer);
  }

  return globals;
}
